import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { CustomDialog } from 'components/blocks/custom-dialog/custom-dialog';
import { Input } from 'components/ui/input';
import { IconName } from 'components/blocks/menu-icon/menu-icon';
import { GroupShareModal } from 'features/chat/components/GroupShareModal';
import { NewProjectModal } from 'features/projects/components/new-project-modal';
import { refetchProject } from 'features/projects/helpers/query-utils';
import { useUpdateProject } from 'features/projects/hooks/use-create-project';
import { sendInvitation } from 'features/group-chat/services/group-services';
import { isGroupChatEnabled } from 'config/feature-flags';
import { useConfig } from 'hooks/use-config';
import { useGroupShareModal } from 'hooks/use-group-share-modal';
import { useToast } from 'hooks/use-toast';
import { hasValidPlanForStartViaShareLink } from 'pages/services/utilityService';
import { deleteConversationById } from 'services/storage-service';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useSubscriptionStore } from 'state/store/subscription';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  markConversationDeletedLocally,
  clearLocallyDeletedConversation,
  removeAgentConversationFromCache,
} from 'features/chat/utils/agent-sidebar-sync';
import { modalOption } from '/models/sidebar';

type ConversationActionType = 'delete' | 'archive' | 'rename' | 'leave';

export function useConversationContextMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { conversationId: conversationIdFromParams } = useParams<{ conversationId?: string }>();
  const { mutateAsync: updateProject } = useUpdateProject();
  const { configurationSettings } = useConfig();
  const { currentPlan } = useSubscriptionStore();

  const flatConversationList = useChatStore((state) => state.sidebarConversationsList);
  const searchConversationsList = useChatStore((state) => state.searchConversationsList);
  const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
  const shareTokens = useChatStore((state) => state.shareTokens);
  const removeConversationFromRecentAndProjects = useChatStore(
    (state) => state.removeConversationFromRecentAndProjects
  );
  const updateConversationTitleInStore = useChatStore(
    (state) => state.updateConversationTitleInStore
  );
  const fetchAndUpdateProjectAllConversations = useChatStore(
    (state) => state.fetchAndUpdateProjectAllConversations
  );
  const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState<{
    id: string | null;
    type: ConversationActionType | null;
  }>({
    id: null,
    type: null,
  });
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [conversationIdToAddToProject, setConversationIdToAddToProject] = useState<
    string | undefined
  >(undefined);
  const [selectedConversationIdForShare, setSelectedConversationIdForShare] = useState<
    string | undefined
  >(undefined);

  const {
    showModal,
    setShowModal,
    shareToken,
    setShareToken,
    handleOpenModal,
    isLoading: isLoadingModal,
  } = useGroupShareModal(selectedConversationIdForShare);

  const findConversation = useCallback(
    (conversationId: string) =>
      flatConversationList.find((c) => c.ConversationId === conversationId) ??
      searchConversationsList.find((c) => c.ConversationId === conversationId),
    [flatConversationList, searchConversationsList]
  );

  const removeConversationFromAllLists = useCallback(
    (conversationId: string) => {
      removeConversationFromRecentAndProjects(conversationId);
      removeAgentConversationFromCache(queryClient, conversationId);
      useChatStore.setState((state) => ({
        searchConversationsList: state.searchConversationsList.filter(
          (item) => item.ConversationId !== conversationId
        ),
      }));
    },
    [removeConversationFromRecentAndProjects, queryClient]
  );

  const handleAddToProject = async (projectId: string, conversationId: string) => {
    try {
      const toProject = sidebarProjectsList.find((p) => p.id === projectId);
      if (!toProject) return;

      await updateProject({
        payload: { projectId, conversationId },
        skipInvalidate: true,
      });
      await fetchAndUpdateProjectAllConversations(configurationSettings, projectId);
      // Project chats also belong in Recent Chats — refresh the list so the row
      // stays with updated ProjectId/color instead of disappearing.
      if (configurationSettings) {
        await fetchSidebarConversations(configurationSettings, {
          page: 0,
          pageSize: 50,
          isGroupConversation: false,
          includeAgentConversations: true,
        });
      }
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MOVE_SUCCESS_DESCRIPTION'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('MOVE_FAILED'),
        description: t('MOVE_FAILED_DESCRIPTION'),
      });
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleRename = async (conversationId: string) => {
    try {
      setIsRenameLoading(true);
      await updateProject({ payload: { conversationId, conversationTitle: newTitle } });
      updateConversationTitleInStore(conversationId, newTitle, { isManual: true });
      setIsRenameDialogOpen(false);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('RENAME_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsRenameDialogOpen(false);
      toast({
        variant: 'destructive',
        title: t('RENAME_FAILED'),
        description: t('RENAME_FAILED_DESCRIPTION'),
      });
      console.error('Error renaming conversation:', error);
    } finally {
      setIsRenameLoading(false);
    }
  };

  const handleArchive = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await updateProject({ payload: { isArchived: true, conversationId } });
      removeConversationFromAllLists(conversationId);
      if (conversationIdFromParams === conversationId) {
        navigate('/chat');
      }
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('ARCHIVE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ARCHIVE_FAILED'),
        description: t('ARCHIVE_FAILED_DESCRIPTION'),
      });
      console.error('Error archiving conversation:', error);
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleDelete = async (conversationId: string) => {
    try {
      setIsLoading(true);
      markConversationDeletedLocally(conversationId);
      await deleteConversationById(conversationId, configurationSettings);
      removeConversationFromAllLists(conversationId);
      if (conversationIdFromParams === conversationId) {
        navigate('/chat');
      }
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('DELETE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      clearLocallyDeletedConversation(conversationId);
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED'),
        description: t('DELETE_FAILED_DESCRIPTION'),
      });
      console.error('Error deleting conversation:', error);
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleLeave = async (conversationId: string) => {
    try {
      setIsLoading(true);
      const currentUser = useCommonStore.getState().user;
      if (!currentUser?.id || !configurationSettings) {
        throw new Error('User or configuration not available');
      }

      const conversation = findConversation(conversationId);
      const payload = {
        invitationType: 'REMOVE_USER' as const,
        userIds: [currentUser.id],
        conversationId,
        conversationTitle: conversation?.ConversationTitle || '',
      } as Parameters<typeof sendInvitation>[0];

      await sendInvitation(payload, configurationSettings);
      removeConversationFromAllLists(conversationId);

      if (conversationIdFromParams === conversationId) {
        navigate('/chat');
      }

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('LEAVE_SUCCESS_DESCRIPTION') || 'You have left the conversation',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('LEAVE_FAILED') || 'Failed to leave',
        description: t('LEAVE_FAILED_DESCRIPTION') || 'Failed to leave the conversation',
      });
      console.error('Error leaving conversation:', error);
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleConfirmAction = () => {
    if (!conversationState.id || !conversationState.type) return;

    switch (conversationState.type) {
      case 'archive':
        void handleArchive(conversationState.id);
        break;
      case 'delete':
        void handleDelete(conversationState.id);
        break;
      case 'rename':
        void handleRename(conversationState.id);
        break;
      case 'leave':
        void handleLeave(conversationState.id);
        break;
    }
  };

  const getMenuOptions = useCallback(
    (conversationId: string, conversationTitle: string, isGroupChat?: boolean): modalOption[] => {
      const shareTokenForConversation = shareTokens[conversationId];
      const hasShareToken = !!shareTokenForConversation;
      const conversation = findConversation(conversationId);
      const currentUser = useCommonStore.getState().user;
      const isCreator = conversation?.CreatedBy === currentUser?.id;

      const baseOptions: modalOption[] = [
        {
          id: 'rename',
          name: t('RENAME'),
          icon: 'Pencil' as IconName,
          callback: () => {
            setIsRenameDialogOpen(true);
            setNewTitle(conversationTitle || '');
            setConversationState({
              id: conversationId,
              type: 'rename',
            });
          },
        },
      ];

      if (isGroupChatEnabled && hasValidPlanForStartViaShareLink(currentPlan)) {
        baseOptions.push({
          id: 'start-group-chat',
          name: hasShareToken
            ? t('ADD_PARTICIPANT_VIA_LINK') || 'Add Participant via link'
            : t('START_GROUP_CHAT') || 'Start group chat',
          icon: 'UserRoundPlus' as IconName,
          isDisabled: isLoadingModal,
          callback: () => {
            setSelectedConversationIdForShare(conversationId);
            handleOpenModal();
          },
        });
      }

      if (!isGroupChat) {
        baseOptions.push({
          id: 'add-to-project',
          type: 'submenu' as const,
          name: t('ADD_TO_PROJECT'),
          icon: 'FolderClosed' as IconName,
          subOptions: [
            {
              id: 'new-project',
              name: t('NEW_PROJECT'),
              icon: 'FolderPlus' as IconName,
              callback: () => {
                setIsAddProjectModalOpen(true);
                setConversationIdToAddToProject(conversationId);
              },
            },
            ...sidebarProjectsList.map((project) => ({
              id: project.id,
              name: project.name,
              icon: 'FolderClosed' as IconName,
              callback: () => void handleAddToProject(project.id, conversationId),
            })),
          ],
        });
        baseOptions.push({
          id: 'archive',
          name: t('ARCHIVE'),
          icon: 'Archive' as IconName,
          callback: () => {
            setIsConfirmDialogOpen(true);
            setConversationState({
              id: conversationId,
              type: 'archive',
            });
          },
        });
      }

      if (isCreator) {
        baseOptions.push({
          id: 'delete',
          name: t('DELETE'),
          icon: 'Trash2' as IconName,
          callback: () => {
            setIsConfirmDialogOpen(true);
            setConversationState({
              id: conversationId,
              type: 'delete',
            });
          },
        });
      } else {
        baseOptions.push({
          id: 'leave',
          name: t('LEAVE') || 'Leave',
          icon: 'LogOut' as IconName,
          callback: () => {
            setIsConfirmDialogOpen(true);
            setConversationState({
              id: conversationId,
              type: 'leave',
            });
          },
        });
      }

      return baseOptions;
    },
    [
      currentPlan,
      findConversation,
      handleOpenModal,
      isLoadingModal,
      shareTokens,
      sidebarProjectsList,
      t,
    ]
  );

  const conversationMenuDialogs = (
    <>
      <CustomDialog
        isOpen={isConfirmDialogOpen}
        isLoading={isLoading}
        setIsOpen={setIsConfirmDialogOpen}
        title={
          conversationState.type === 'delete'
            ? t('DELETE')
            : conversationState.type === 'leave'
              ? t('LEAVE') || 'Leave'
              : t('CONFIRM_ACTION')
        }
        onConfirm={handleConfirmAction}
        confirmText={t('YES')}
        cancelText={t('CANCEL')}
        description=""
      >
        <p>
          {conversationState.type === 'delete'
            ? t('DELETE_CONFIRMATION_DESCRIPTION') || t('CONFIRM_ACTION_DESCRIPTION')
            : conversationState.type === 'leave'
              ? t('LEAVE_CONFIRMATION_DESCRIPTION') ||
              'Are you sure you want to leave this conversation?'
              : t('CONFIRM_ACTION_DESCRIPTION')}
        </p>
      </CustomDialog>

      <CustomDialog
        isOpen={isRenameDialogOpen}
        isLoading={isRenameLoading}
        setIsOpen={setIsRenameDialogOpen}
        title={t('RENAME')}
        onConfirm={handleConfirmAction}
        confirmText={t('SAVE')}
        cancelText={t('CANCEL')}
        description=""
        isConfirmDisabled={newTitle.trim() === ''}
      >
        <Input
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          placeholder={t('ENTER_NEW_TITLE')}
          maxLength={50}
          className="h-10 w-full rounded-3xl border border-primary bg-background pr-8 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </CustomDialog>

      <NewProjectModal
        callback={() => {
          refetchProject(queryClient, '');
          if (configurationSettings) {
            void fetchSidebarConversations(configurationSettings, {
              page: 0,
              pageSize: 50,
              isGroupConversation: false,
              includeAgentConversations: true,
            });
          }
        }}
        newConversatioId={conversationIdToAddToProject}
        isOpen={isAddProjectModalOpen}
        onClose={() => setIsAddProjectModalOpen(false)}
      />

      {isGroupChatEnabled && (
        <GroupShareModal
          showModal={showModal}
          setShowModal={setShowModal}
          shareToken={shareToken}
          setShareToken={setShareToken}
          isLoadingConversation={isLoadingModal}
        />
      )}
    </>
  );

  return {
    getMenuOptions,
    conversationMenuDialogs,
  };
}
